import { useState } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { videos, getFeaturedVideos } from '@/mocks/videos';
import { reviews, stats } from '@/mocks/reviews';
import { faqs } from '@/mocks/faqs';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

function HeroSection() {
  return (
    <section className="relative w-full h-[520px] md:h-[680px] flex items-center justify-center overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=Cinematic%20dark%20theater%20with%20warm%20golden%20spotlight%20on%20stage%2C%20elegant%20red%20curtains%2C%20film%20projector%20beam%2C%20luxurious%20movie%20theater%20atmosphere%2C%20dramatic%20lighting%2C%20premium%20entertainment%20aesthetic&width=1800&height=900&seq=home-hero-bg&orientation=landscape"
        alt="精品视频精选平台"
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
      <div className="relative z-10 w-full max-w-4xl mx-auto px-4 text-center">
        <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-5 leading-tight">
          精品视频<span className="text-accent-400">精选</span>平台
        </h1>
        <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-8 leading-relaxed">
          精品视频汇聚全网高清优质内容，涵盖影视、教育、娱乐、纪录片等多元类型。每一部精品视频均经严格筛选，画质清晰、内容精良，满足您对高品质视听体验的极致追求。
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/category/yingshi"
            className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-full transition-all duration-300 whitespace-nowrap text-sm"
          >
            立即探索
            <i className="ri-arrow-right-line ml-2 w-4 h-4 inline-flex items-center justify-center align-middle"></i>
          </Link>
          <Link
            to="/about"
            className="px-8 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-full backdrop-blur-sm transition-all duration-300 whitespace-nowrap text-sm border border-white/20"
          >
            了解更多
          </Link>
        </div>
        <div className="mt-10 flex items-center justify-center gap-6 text-white/60 text-xs">
          <span className="flex items-center gap-1">
            <i className="ri-checkbox-circle-fill text-accent-400 w-4 h-4 flex items-center justify-center"></i>
            5000+ 精品视频
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-checkbox-circle-fill text-accent-400 w-4 h-4 flex items-center justify-center"></i>
            4K超高清画质
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-checkbox-circle-fill text-accent-400 w-4 h-4 flex items-center justify-center"></i>
            每日更新
          </span>
        </div>
      </div>
    </section>
  );
}

function CategorySection() {
  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
            探索内容分类
          </h2>
          <p className="text-foreground-500 text-sm max-w-xl mx-auto">
            八大精品内容分类，精准匹配您的观看偏好
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className="group bg-background-50 border border-background-200/70 rounded-lg p-5 hover:border-primary-300 transition-all duration-300 no-underline"
            >
              <div className="w-11 h-11 rounded-lg bg-primary-50 flex items-center justify-center mb-3 group-hover:bg-primary-100 transition-colors">
                <i className={`${cat.icon} text-primary-500 text-xl w-5 h-5 flex items-center justify-center`}></i>
              </div>
              <h3 className="text-sm font-semibold text-foreground-900 mb-1">{cat.name}</h3>
              <p className="text-xs text-foreground-500">{cat.count} 部视频</p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturedSection() {
  const featured = getFeaturedVideos(6);

  return (
    <section className="py-14 md:py-20 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
              精选推荐
            </h2>
            <p className="text-foreground-500 text-sm">编辑团队精心挑选的优质内容</p>
          </div>
          <Link
            to="/category/yingshi"
            className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1 whitespace-nowrap"
          >
            查看全部
            <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {featured.map((video) => (
            <Link
              key={video.id}
              to={`/article/${video.id}`}
              className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/50 hover:border-primary-200 transition-all duration-300 no-underline"
            >
              <div className="relative w-full aspect-video overflow-hidden">
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-0.5 rounded">
                  {video.duration}
                </div>
                <div className="absolute top-2 left-2 bg-primary-500 text-white text-xs px-2 py-0.5 rounded">
                  {video.quality}
                </div>
              </div>
              <div className="p-4">
                <h3 className="text-sm font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                  {video.title}
                </h3>
                <p className="text-xs text-foreground-500 line-clamp-2 mb-3">{video.description}</p>
                <div className="flex items-center justify-between text-xs text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line w-3.5 h-3.5 flex items-center justify-center"></i>
                    {(video.views / 10000).toFixed(1)}万
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500 w-3.5 h-3.5 flex items-center justify-center"></i>
                    {video.rating}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function StatsSection() {
  return (
    <section className="py-12 md:py-16 bg-primary-500">
      <div className="max-w-5xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="text-3xl md:text-4xl font-heading font-bold text-white mb-1">
                {stat.value}
              </div>
              <div className="text-sm text-white/70">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ReviewSection() {
  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
            用户口碑
          </h2>
          <p className="text-foreground-500 text-sm max-w-xl mx-auto">
            听听用户们怎么说
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {reviews.map((review) => (
            <div
              key={review.id}
              className="bg-background-50 border border-background-200/70 rounded-lg p-5"
            >
              <div className="flex items-center gap-3 mb-4">
                <img
                  src={review.avatar}
                  alt={review.name}
                  className="w-11 h-11 rounded-full object-cover"
                  loading="lazy"
                />
                <div>
                  <div className="text-sm font-semibold text-foreground-900">{review.name}</div>
                  <div className="text-xs text-foreground-500">{review.role}</div>
                </div>
              </div>
              <div className="flex gap-0.5 mb-3">
                {Array.from({ length: review.rating }).map((_, i) => (
                  <i key={i} className="ri-star-fill text-accent-500 text-xs w-3.5 h-3.5 flex items-center justify-center"></i>
                ))}
              </div>
              <p className="text-xs text-foreground-600 leading-relaxed line-clamp-4">{review.content}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="py-14 md:py-20 bg-background-100">
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
            常见问题
          </h2>
          <p className="text-foreground-500 text-sm">关于精品视频精选平台的常见疑问</p>
        </div>
        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden"
            >
              <button
                className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-background-100 transition-colors"
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                aria-expanded={openIndex === index}
              >
                <span className="text-sm font-medium text-foreground-900 pr-4">{faq.question}</span>
                <i
                  className={`ri-arrow-down-s-line w-5 h-5 flex items-center justify-center text-foreground-500 transition-transform duration-200 flex-shrink-0 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>
              {openIndex === index && (
                <div className="px-5 pb-4">
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function NewsletterSection() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
    if (honeypotValue) {
      setStatus('success');
      setMessage('感谢订阅！您將第一时间收到最新视频资讯。');
      return;
    }
    setStatus('loading');
    try {
      const res = await fetch('https://readdy.ai/api/form/d9rcqd6e3oq4jqumtvl0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email }).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        setMessage('感谢订阅！您将第一时间收到最新视频资讯。');
        setEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '';
        setStatus('error');
        setMessage(serverMsg || '订阅失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setMessage('网络错误，请检查连接后重试。');
    }
  };

  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="max-w-2xl mx-auto px-4 md:px-6 text-center">
        <div className="w-14 h-14 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-5">
          <i className="ri-mail-send-line text-accent-500 text-2xl w-6 h-6 flex items-center justify-center"></i>
        </div>
        <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
          订阅精品视频资讯
        </h2>
        <p className="text-foreground-500 text-sm mb-8">
          第一时间获取最新精品视频上线通知和精选内容推荐
        </p>
        <form
          onSubmit={handleSubmit}
          data-readdy-form
          className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
        >
          <div className="ab-9873-field-wrap">
            <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
          </div>
          <input
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="请输入您的邮箱地址"
            required
            className="flex-1 px-5 py-3 rounded-full border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
          />
          <button
            type="submit"
            disabled={status === 'loading'}
            className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-full transition-all duration-300 whitespace-nowrap text-sm disabled:opacity-60 cursor-pointer"
          >
            {status === 'loading' ? '订阅中...' : '立即订阅'}
          </button>
        </form>
        {status === 'success' && (
          <p className="mt-4 text-sm text-secondary-600">{message}</p>
        )}
        {status === 'error' && (
          <p className="mt-4 text-sm text-primary-500">{message}</p>
        )}
      </div>
    </section>
  );
}

function CtaSection() {
  return (
    <section className="relative py-16 md:py-24 overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=Warm%20cinematic%20abstract%20background%20with%20soft%20golden%20light%20streaks%2C%20elegant%20gradient%2C%20premium%20luxury%20feel%2C%20dark%20and%20warm%20atmosphere%2C%20film%20projector%20light%20effect&width=1800&height=600&seq=home-cta-bg&orientation=landscape"
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-black/60"></div>
      <div className="relative z-10 max-w-2xl mx-auto px-4 text-center">
        <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
          开启您的精品视听之旅
        </h2>
        <p className="text-white/70 text-sm mb-8 max-w-lg mx-auto">
          立即探索海量精品视频资源，高清画质、精选内容，尽在精品视频精选平台
        </p>
        <Link
          to="/category/yingshi"
          className="inline-flex items-center gap-2 px-8 py-3 bg-accent-500 hover:bg-accent-600 text-foreground-950 font-semibold rounded-full transition-all duration-300 whitespace-nowrap text-sm"
        >
          立即开始观看
          <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
        </Link>
      </div>
    </section>
  );
}

export default function HomePage() {
  const siteName = '精品视频精选平台';
  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: siteName,
        url: 'https://www.jingpinsp.com',
        logo: 'https://www.jingpinsp.com/logo.png',
        description: '精品视频汇聚全网高清优质内容，涵盖影视、教育、娱乐、纪录片等多元类型。',
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '400-888-9999',
          contactType: 'customer service',
          availableLanguage: 'Chinese',
        },
        address: {
          '@type': 'PostalAddress',
          addressLocality: '北京',
          addressRegion: '朝阳区',
          streetAddress: '科技园区88号',
          addressCountry: 'CN',
        },
      },
      {
        '@type': 'WebSite',
        name: siteName,
        url: 'https://www.jingpinsp.com',
        potentialAction: {
          '@type': 'SearchAction',
          target: 'https://www.jingpinsp.com/search?q={search_term_string}',
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'WebPage',
        name: `${siteName} - 高清优质内容一站汇聚`,
        description: '精品视频汇聚全网高清优质内容，涵盖影视、教育、娱乐、纪录片等多元类型。每一部精品视频均经严格筛选，画质清晰、内容精良。',
        url: 'https://www.jingpinsp.com',
        isPartOf: { '@type': 'WebSite', name: siteName },
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="精品视频精选平台｜高清优质内容一站汇聚"
        description="精品视频汇聚全网高清优质内容，涵盖影视、教育、娱乐、纪录片等多元类型。每一部精品视频均经严格筛选，画质清晰、内容精良，满足您对高品质视听体验的极致追求。立即探索海量精品视频资源，开启全新观看体验。"
        keywords="精品视频,精品视频在线观看,高清精品视频,精品视频推荐,优质视频内容,精品短视频合集,精品视频免费播放"
        canonicalUrl="https://www.jingpinsp.com"
        ogTitle="精品视频精选平台｜高清优质内容一站汇聚"
        ogDescription="精品视频汇聚全网高清优质内容，涵盖影视、教育、娱乐、纪录片等多元类型。画质清晰、内容精良，满足您对高品质视听体验的极致追求。"
        ogImage="https://readdy.ai/api/search-image?query=Cinematic%20dark%20theater%20with%20warm%20golden%20spotlight%2C%20elegant%20movie%20atmosphere%2C%20premium%20entertainment%2C%20luxurious%20cinema%20aesthetic&width=1200&height=630&seq=home-og&orientation=landscape"
        schemaData={schemaData}
      />
      <Navbar />
      <main>
        <HeroSection />
        <CategorySection />
        <FeaturedSection />
        <StatsSection />
        <ReviewSection />
        <FAQSection />
        <NewsletterSection />
        <CtaSection />
      </main>
      <Footer />
    </>
  );
}